import React from "react";
import { Routes, Route, NavLink, Link } from "react-router-dom";
import "./App.css";

const COMPANY_NAME = "Istithmar LLC";
const ADDRESS = "127 North Higgins Avenue, STE 307D 1767, Missoula, Montana, 59802";
const PHONE = "+1 (406) 229-9500";
const EMAIL = "support@istithmar-llc.com";

/* ─── Shared arrow icon ─────────────────────────────────────── */
function ArrowRight({ className = "w-4 h-4" }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
    </svg>
  );
}

function ExternalLink({ className = "w-4 h-4" }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
    </svg>
  );
}

/* ─── Header ────────────────────────────────────────────────── */
function Header() {
  return (
    <header className="sticky top-0 z-50 bg-zinc-950/85 backdrop-blur-md border-b border-zinc-800/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <NavLink to="/" className="flex items-center gap-2.5 group" aria-label="Istithmar LLC home">
          <div className="h-8 w-8 rounded-lg bg-blue-600 text-white flex items-center justify-center text-xs font-bold tracking-tight select-none">
            IL
          </div>
          <span className="text-zinc-100 font-semibold text-sm tracking-tight hidden sm:block">{COMPANY_NAME}</span>
        </NavLink>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1" aria-label="Main navigation">
          {[
            { to: "/", label: "Home" },
            { to: "/products", label: "Products" },
            { to: "/policies", label: "Policies" },
            { to: "/contact", label: "Contact" },
          ].map(({ to, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              className={({ isActive }) =>
                `px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "text-zinc-50 bg-zinc-800"
                    : "text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/60"
                }`
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>

        {/* CTA */}
        <Link
          to="/products"
          className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          Our Products
        </Link>
      </div>
    </header>
  );
}

/* ─── Footer ────────────────────────────────────────────────── */
function Footer() {
  return (
    <footer className="border-t border-zinc-800 bg-zinc-950">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-12 pb-8">
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-8 mb-10">
          {/* Brand column */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="h-8 w-8 rounded-lg bg-blue-600 text-white flex items-center justify-center text-xs font-bold select-none">
                IL
              </div>
              <span className="text-zinc-100 font-semibold text-sm">{COMPANY_NAME}</span>
            </div>
            <p className="text-zinc-500 text-sm leading-6 max-w-xs">
              Open-source software house building tools on public data, transparent models, and honest code.
            </p>
          </div>

          {/* Nav column */}
          <div>
            <div className="text-zinc-300 text-xs font-semibold uppercase tracking-widest mb-4">Pages</div>
            <ul className="space-y-2.5">
              {[
                { to: "/", label: "Home" },
                { to: "/products", label: "Products" },
                { to: "/policies", label: "Policies" },
                { to: "/contact", label: "Contact" },
              ].map(({ to, label }) => (
                <li key={to}>
                  <NavLink
                    to={to}
                    end={to === "/"}
                    className="text-zinc-500 hover:text-zinc-300 text-sm transition-colors"
                  >
                    {label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact column */}
          <div>
            <div className="text-zinc-300 text-xs font-semibold uppercase tracking-widest mb-4">Contact</div>
            <ul className="space-y-2.5 text-sm">
              <li>
                <a href={`mailto:${EMAIL}`} className="text-zinc-500 hover:text-zinc-300 break-all transition-colors">
                  {EMAIL}
                </a>
              </li>
              <li>
                <a href={`tel:+14062299500`} className="text-zinc-500 hover:text-zinc-300 transition-colors">
                  {PHONE}
                </a>
              </li>
              <li className="text-zinc-600 text-xs leading-5">{ADDRESS}</li>
            </ul>
          </div>
        </div>

        <div className="border-t border-zinc-800/60 pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="text-zinc-600 text-xs">
            &copy; {new Date().getFullYear()} {COMPANY_NAME}. All rights reserved.
          </p>
          <NavLink to="/policies" className="text-zinc-600 hover:text-zinc-400 text-xs transition-colors">
            Cancellation Policy &amp; Promotions Terms
          </NavLink>
        </div>
      </div>
    </footer>
  );
}

/* ─── Home ──────────────────────────────────────────────────── */
function Home() {
  return (
    <main>
      {/* Hero */}
      <section className="hero-bg min-h-[calc(100dvh-4rem)] flex flex-col justify-center px-4 sm:px-6">
        <div className="max-w-6xl mx-auto w-full py-20">
          <div className="max-w-2xl">
            <span className="inline-flex items-center px-3 py-1 rounded-full border border-zinc-700/80 bg-zinc-900/60 text-zinc-400 text-xs font-medium tracking-wide mb-7">
              Open Source Software House
            </span>

            <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-bold text-zinc-50 tracking-tight leading-[1.1]">
              Open-source tools<br />
              that earn their keep.
            </h1>

            <p className="mt-5 text-zinc-400 text-base sm:text-lg leading-relaxed max-w-lg">
              Real software on public data and open models. Transparent by design, revenue-positive from launch.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/products"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 active:scale-[0.98] text-white font-medium rounded-lg transition-all text-sm"
              >
                See our products
                <ArrowRight />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-5 py-2.5 border border-zinc-700 hover:border-zinc-500 text-zinc-300 hover:text-zinc-50 font-medium rounded-lg transition-colors text-sm"
              >
                Contact us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="px-4 sm:px-6 pb-24">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-zinc-50 font-semibold text-xl mb-6">Current products</h2>

          <div className="grid md:grid-cols-2 gap-4">
            {/* personalCFA */}
            <a
              href="https://personalcfa.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative rounded-2xl border border-zinc-800 bg-zinc-900 hover:border-zinc-600 hover:bg-zinc-800/70 transition-all duration-200 overflow-hidden"
            >
              <div className="relative h-44 overflow-hidden">
                <img
                  src="https://picsum.photos/seed/finance-market-charts/900/400"
                  alt="personalCFA product"
                  className="w-full h-full object-cover opacity-40 group-hover:opacity-50 transition-opacity duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/60 to-transparent" />
                <div className="absolute top-4 right-4">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-950/90 border border-emerald-700/60 text-emerald-400 text-xs font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-slow" />
                    Live
                  </span>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-zinc-50 font-bold text-lg">personalCFA</h3>
                  <span className="text-zinc-600 text-xs font-mono">personalcfa.com</span>
                </div>
                <p className="text-zinc-400 text-sm leading-relaxed mb-5">
                  AI-assisted personal finance analysis on open historical market data. Explainable outputs inspired by Lynch and Buffett, for research and learning.
                </p>
                <span className="inline-flex items-center gap-1.5 text-blue-400 text-sm font-medium group-hover:text-blue-300 transition-colors">
                  Open personalcfa.com
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </div>
            </a>

            {/* Aperture - Open Agency OS */}
            <a
              href="https://github.com/SahibYar/open-agency-os"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative rounded-2xl border border-zinc-800 bg-zinc-900 hover:border-zinc-600 hover:bg-zinc-800/70 transition-all duration-200 overflow-hidden"
            >
              <div className="relative h-44 overflow-hidden">
                <img
                  src="https://picsum.photos/seed/agency-crm-dashboard-tech/900/400"
                  alt="Aperture - Open Agency OS"
                  className="w-full h-full object-cover opacity-30 group-hover:opacity-40 transition-opacity duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/60 to-transparent" />
                <div className="absolute top-4 right-4">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-violet-950/90 border border-violet-700/60 text-violet-400 text-xs font-medium">
                    Open Source
                  </span>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-zinc-50 font-bold text-lg">Aperture</h3>
                  <span className="text-zinc-600 text-xs font-mono">Open Agency OS</span>
                </div>
                <p className="text-zinc-400 text-sm leading-relaxed mb-5">
                  Self-hosted alternative to GoHighLevel. Combines CRM, marketing automation, inbox, booking, and workflow tools into one Docker-based agency OS.
                </p>
                <span className="inline-flex items-center gap-1.5 text-violet-400 text-sm font-medium group-hover:text-violet-300 transition-colors">
                  View on GitHub
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="border-t border-zinc-800 px-4 sm:px-6 py-24">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-px bg-zinc-800/60 rounded-2xl overflow-hidden">
            {[
              {
                num: "01",
                title: "Open by default",
                desc: "Every model, every formula, and every data source is documented and verifiable. No proprietary black boxes.",
              },
              {
                num: "02",
                title: "Public data only",
                desc: "We rely exclusively on publicly available information. Our analysis can be independently verified by anyone.",
              },
              {
                num: "03",
                title: "Built to sustain",
                desc: "Free tools, honest paid plans. We sustain what we build without investor dependency or lock-in.",
              },
            ].map(({ num, title, desc }) => (
              <div
                key={num}
                className="bg-zinc-950 hover:bg-zinc-900/50 transition-colors duration-200 p-8 sm:p-10"
              >
                <div className="text-zinc-700 font-mono text-xs tracking-widest mb-5">{num}</div>
                <h3 className="text-zinc-100 font-semibold text-base mb-3">{title}</h3>
                <p className="text-zinc-500 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="px-4 sm:px-6 pb-24">
        <div className="max-w-6xl mx-auto grid sm:grid-cols-2 gap-4">
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-7 flex flex-col justify-between gap-5">
            <div>
              <h2 className="text-zinc-50 font-bold text-lg mb-1.5">Try personalCFA</h2>
              <p className="text-zinc-400 text-sm">AI-powered finance analysis, free to start.</p>
            </div>
            <a
              href="https://personalcfa.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="self-start inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 active:scale-[0.98] text-white font-medium rounded-lg transition-all text-sm"
            >
              Go to personalcfa.com
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-7 flex flex-col justify-between gap-5">
            <div>
              <h2 className="text-zinc-50 font-bold text-lg mb-1.5">Self-host Aperture</h2>
              <p className="text-zinc-400 text-sm">Replace GoHighLevel with your own agency OS. Free forever.</p>
            </div>
            <a
              href="https://github.com/SahibYar/open-agency-os"
              target="_blank"
              rel="noopener noreferrer"
              className="self-start inline-flex items-center gap-2 px-4 py-2 border border-zinc-700 hover:border-zinc-500 text-zinc-300 hover:text-zinc-50 font-medium rounded-lg transition-colors text-sm"
            >
              View on GitHub
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}

/* ─── Products ──────────────────────────────────────────────── */
function Products() {
  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-20">
      {/* Page header */}
      <div className="mb-14">
        <h1 className="text-3xl sm:text-4xl font-bold text-zinc-50 tracking-tight mb-3">Products</h1>
        <p className="text-zinc-400 text-base max-w-lg leading-relaxed">
          Software we build and ship. Open source where it matters, revenue-positive to stay sustainable.
        </p>
      </div>

      {/* personalCFA */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900 overflow-hidden mb-6">
        {/* Header image */}
        <div className="relative h-52 overflow-hidden">
          <img
            src="https://picsum.photos/seed/stock-market-investing-data/1200/500"
            alt="personalCFA - AI-assisted finance analysis"
            className="w-full h-full object-cover opacity-35"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 px-8 pb-6 flex items-end justify-between">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold text-sm shadow-lg">
                pC
              </div>
              <div>
                <div className="text-zinc-50 font-bold text-2xl leading-none">personalCFA</div>
                <div className="text-zinc-400 text-xs font-mono mt-0.5">personalcfa.com</div>
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-950/90 border border-emerald-700/60 text-emerald-400 text-xs font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-slow" />
              Live
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 sm:p-8">
          <p className="text-zinc-400 leading-7 mb-8 max-w-2xl">
            PersonalCFA brings AI-assisted analysis to personal finance education. Built on open, historical market data with investment approaches inspired by Peter Lynch and Warren Buffett. All outputs are explainable and intended for learning and research, not financial advice.
          </p>

          {/* Features grid */}
          <div className="grid sm:grid-cols-3 gap-3 mb-8">
            {[
              { title: "AI-assisted analysis", body: "Powered by documented, open financial models you can verify." },
              { title: "Transparent data only", body: "Exclusively publicly available market data. Nothing gated or proprietary." },
              { title: "Educational focus", body: "Designed for learning and research. Outputs are clear and explainable." },
            ].map(({ title, body }) => (
              <div key={title} className="p-4 rounded-xl bg-zinc-950 border border-zinc-800">
                <div className="text-zinc-200 font-medium text-sm mb-1.5">{title}</div>
                <div className="text-zinc-500 text-xs leading-5">{body}</div>
              </div>
            ))}
          </div>

          <a
            href="https://personalcfa.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 active:scale-[0.98] text-white font-medium rounded-lg transition-all text-sm"
          >
            Open personalcfa.com
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* Aperture */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900 overflow-hidden mb-6">
        <div className="relative h-52 overflow-hidden">
          <img
            src="https://picsum.photos/seed/agency-crm-dashboard-tech/1200/500"
            alt="Aperture - Open Agency OS"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 px-8 pb-6 flex items-end justify-between">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-violet-500 to-violet-700 flex items-center justify-center text-white font-bold text-sm shadow-lg">
                Ap
              </div>
              <div>
                <div className="text-zinc-50 font-bold text-2xl leading-none">Aperture</div>
                <div className="text-zinc-400 text-xs font-mono mt-0.5">Open Agency OS</div>
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-violet-950/90 border border-violet-700/60 text-violet-400 text-xs font-medium">
              Open Source
            </span>
          </div>
        </div>

        <div className="p-6 sm:p-8">
          <p className="text-zinc-400 leading-7 mb-8 max-w-2xl">
            A self-hosted, open-source alternative to GoHighLevel. Instead of cloning a proprietary product, Aperture integrates mature existing open-source systems — Twenty, Mautic, Chatwoot, Cal.com, n8n, and WordPress — into one unified agency operating system deployable via Docker Compose.
          </p>

          <div className="grid sm:grid-cols-3 gap-3 mb-8">
            {[
              { title: "Self-hosted and owned", body: "Full data ownership. Deploy on any VPS. No per-seat SaaS pricing." },
              { title: "Modular by design", body: "Docker Compose profiles let you enable only what you need — CRM, inbox, booking, marketing, pages." },
              { title: "Pre-built automations", body: "n8n workflows for form-to-CRM, speed-to-lead SMS, booking sync, and inbox context out of the box." },
            ].map(({ title, body }) => (
              <div key={title} className="p-4 rounded-xl bg-zinc-950 border border-zinc-800">
                <div className="text-zinc-200 font-medium text-sm mb-1.5">{title}</div>
                <div className="text-zinc-500 text-xs leading-5">{body}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 mb-8">
            {["Twenty CRM", "Mautic", "Chatwoot", "Cal.com", "n8n", "WordPress", "Docker Compose", "Caddy"].map((tag) => (
              <span key={tag} className="px-2.5 py-1 rounded-md bg-zinc-800 border border-zinc-700 text-zinc-400 text-xs font-mono">
                {tag}
              </span>
            ))}
          </div>

          <a
            href="https://github.com/SahibYar/open-agency-os"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-zinc-700 hover:border-zinc-500 text-zinc-300 hover:text-zinc-50 active:scale-[0.98] font-medium rounded-lg transition-all text-sm"
          >
            View on GitHub
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>

      <p className="text-zinc-600 text-sm">
        More products are in development. We build in the open and ship when it's ready.
      </p>
    </main>
  );
}

/* ─── Policies ──────────────────────────────────────────────── */
function Policies() {
  return (
    <main className="max-w-3xl mx-auto px-4 sm:px-6 py-20">
      <h1 className="text-3xl sm:text-4xl font-bold text-zinc-50 tracking-tight mb-12">Policies</h1>

      <div className="space-y-0 divide-y divide-zinc-800">
        <section className="pb-10">
          <h2 className="text-zinc-200 font-semibold text-lg mb-3">Customer Service</h2>
          <p className="text-zinc-400 leading-7 text-sm">
            For support, reach us by email at{" "}
            <a href={`mailto:${EMAIL}`} className="text-blue-400 hover:text-blue-300 underline underline-offset-2">
              {EMAIL}
            </a>{" "}
            or by phone at{" "}
            <a href="tel:+14062299500" className="text-blue-400 hover:text-blue-300">
              {PHONE}
            </a>
            . We respond within one business day.
          </p>
        </section>

        <section className="py-10">
          <h2 className="text-zinc-200 font-semibold text-lg mb-3">Cancellation Policy</h2>
          <p className="text-zinc-400 leading-7 text-sm">
            The current month's subscription payment is non-refundable. After cancellation, no further charges apply from the next billing cycle.
          </p>
        </section>

        <section className="py-10">
          <h2 className="text-zinc-200 font-semibold text-lg mb-3">Promotions: Terms and Conditions</h2>
          <p className="text-zinc-400 leading-7 text-sm">
            All calculations are based on historical data using open-source financial models and strategies inspired by investors such as Peter Lynch and Warren Buffett. Our analysis is AI-generated. Customers must perform their own due diligence before making any financial decisions. We do not use proprietary data; all data is publicly available.
          </p>
        </section>

        <section className="pt-10">
          <p className="text-zinc-600 text-xs leading-6">
            Nothing on this website constitutes financial advice. All information is provided for educational and research purposes only.
          </p>
        </section>
      </div>
    </main>
  );
}

/* ─── Contact ───────────────────────────────────────────────── */
function Contact() {
  return (
    <main className="max-w-4xl mx-auto px-4 sm:px-6 py-20">
      <div className="mb-12">
        <h1 className="text-3xl sm:text-4xl font-bold text-zinc-50 tracking-tight mb-3">Contact</h1>
        <p className="text-zinc-400 text-base">We respond within one business day.</p>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-10">
        <a
          href={`mailto:${EMAIL}`}
          className="group p-6 rounded-2xl border border-zinc-800 bg-zinc-900 hover:border-zinc-600 hover:bg-zinc-800/70 transition-all duration-200"
        >
          <div className="text-zinc-600 text-xs font-medium mb-3">Email</div>
          <div className="text-blue-400 group-hover:text-blue-300 text-sm font-medium break-all transition-colors leading-5">
            {EMAIL}
          </div>
        </a>

        <a
          href="tel:+14062299500"
          className="group p-6 rounded-2xl border border-zinc-800 bg-zinc-900 hover:border-zinc-600 hover:bg-zinc-800/70 transition-all duration-200"
        >
          <div className="text-zinc-600 text-xs font-medium mb-3">Phone</div>
          <div className="text-blue-400 group-hover:text-blue-300 text-sm font-medium transition-colors">
            {PHONE}
          </div>
        </a>

        <div className="p-6 rounded-2xl border border-zinc-800 bg-zinc-900">
          <div className="text-zinc-600 text-xs font-medium mb-3">Address</div>
          <div className="text-zinc-400 text-sm leading-6">{ADDRESS}</div>
        </div>
      </div>

      <div className="p-6 rounded-2xl border border-zinc-800 bg-zinc-900/50">
        <p className="text-zinc-500 text-sm leading-6">
          For product support, billing questions, or general inquiries, email us at{" "}
          <a href={`mailto:${EMAIL}`} className="text-blue-400 hover:text-blue-300 underline underline-offset-2">
            {EMAIL}
          </a>
          . For urgent issues, call us directly.
        </p>
      </div>
    </main>
  );
}

/* ─── App root ──────────────────────────────────────────────── */
export default function App() {
  return (
    <div className="min-h-screen flex flex-col bg-zinc-950">
      <Header />
      <div className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<Products />} />
          <Route path="/policies" element={<Policies />} />
          <Route path="/contact" element={<Contact />} />
        </Routes>
      </div>
      <Footer />
    </div>
  );
}
